import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

interface LikedPostItem {
  post_id: number;
  title: string;
  category: string;
  views: number;
  likes: number;
  liked_at: string;
  author_nickname: string;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const categoryLabels: Record<string, string> = {
  community: "자유게시판",
  issue: "실시간 이슈",
  scam_report: "먹튀제보",
  safety: "안전정보",
  sports: "스포츠분석",
  betting: "베팅가이드",
};

export default function LikeHistory() {
  const { userId } = useParams<{ userId?: string }>();
  const { user, loading: authLoading } = useAuth();
  const targetUserId = userId || user?.id;
  const isOwnProfile = !userId || (user && userId === user.id);

  const [likedPosts, setLikedPosts] = useState<LikedPostItem[]>([]);
  const [profile, setProfile] = useState<{ nickname: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchLikedPosts = useCallback(async () => {
    if (!targetUserId) return;

    try {
      const [
        { data: likesData, error: likesError },
        { data: profileData },
      ] = await Promise.all([
        supabase
          .from("post_likes")
          .select("post_id, created_at")
          .eq("user_id", targetUserId)
          .order("created_at", { ascending: false }),
        supabase
          .from("profiles")
          .select("nickname")
          .eq("id", targetUserId)
          .maybeSingle(),
      ]);

      if (likesError) throw likesError;

      if (profileData) {
        setProfile(profileData);
      }

      if (likesData && likesData.length > 0) {
        const postIds = [...new Set(likesData.map((l) => l.post_id))];
        const { data: postsData } = await supabase
          .from("posts")
          .select("id, title, category, views, likes, author_id")
          .in("id", postIds);

        const postMap = new Map<number, typeof postsData[0]>();
        (postsData || []).forEach((p) => postMap.set(p.id, p));

        const authorIds = [...new Set((postsData || []).map((p) => p.author_id).filter(Boolean))];
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, nickname")
          .in("id", authorIds);

        const nicknameMap = new Map<string, string>();
        (profilesData || []).forEach((p) => nicknameMap.set(p.id, p.nickname || "익명"));

        setLikedPosts(
          likesData
            .filter((l) => postMap.has(l.post_id))
            .map((l) => {
              const post = postMap.get(l.post_id)!;
              return {
                post_id: post.id,
                title: post.title,
                category: post.category,
                views: post.views || 0,
                likes: post.likes || 0,
                liked_at: l.created_at,
                author_nickname: nicknameMap.get(post.author_id) || "익명",
              };
            })
        );
      }
    } catch {
      setError("좋아요 내역을 불러오지 못했습니다");
    } finally {
      setLoading(false);
    }
  }, [targetUserId]);

  useEffect(() => {
    if (targetUserId) {
      fetchLikedPosts();
    }
  }, [targetUserId, fetchLikedPosts]);

  const displayName = profile?.nickname || "회원";
  const backLink = isOwnProfile ? "/profile" : `/profile/${targetUserId}`;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-foreground-600">
          <span className="w-4 h-4 flex items-center justify-center animate-spin">
            <i className="ri-loader-4-line" />
          </span>
          <span className="text-sm">로딩 중...</span>
        </div>
      </div>
    );
  }

  if (!user && !userId) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
            <span className="w-8 h-8 flex items-center justify-center text-foreground-600">
              <i className="ri-lock-line text-2xl" />
            </span>
          </div>
          <h2 className="text-lg font-bold text-foreground-700 mb-2">로그인이 필요합니다</h2>
          <p className="text-sm text-foreground-600 mb-4">좋아요 내역은 로그인 후 이용할 수 있습니다</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            로그인하러 가기
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <div className="max-w-[800px] mx-auto px-3 md:px-4 py-6">
        {/* Page Title */}
        <div className="flex items-center gap-3 mb-6">
          <Link
            to={backLink}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 border border-background-300 text-foreground-600 hover:text-foreground-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
          </Link>
          <h1 className="text-lg font-bold text-foreground-700">
            {isOwnProfile ? "내 좋아요 내역" : `${displayName}님의 좋아요 내역`}
          </h1>
        </div>

        {error && (
          <div className="bg-background-100 border border-red-500/20 rounded-xl px-5 py-4 mb-6 flex items-center justify-between">
            <span className="text-sm text-red-400">{error}</span>
            <button
              onClick={fetchLikedPosts}
              className="px-3 py-1.5 text-xs bg-red-500/10 text-red-400 rounded-md hover:bg-red-500/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              재시도
            </button>
          </div>
        )}

        {!loading && likedPosts.length === 0 && (
          <div className="bg-background-100 rounded-xl border border-background-300 px-5 md:px-6 py-14 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <span className="w-7 h-7 flex items-center justify-center text-foreground-600">
                <i className="ri-heart-2-line text-xl" />
              </span>
            </div>
            <h2 className="text-base font-bold text-foreground-700 mb-1">
              좋아요한 글이 없습니다
            </h2>
            <p className="text-sm text-foreground-600 mb-5">
              커뮤니티에서 마음에 드는 글에 좋아요를 눌러보세요
            </p>
            <Link
              to="/community"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-community-line" />
              </span>
              커뮤니티 가기
            </Link>
          </div>
        )}

        {likedPosts.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-foreground-600">
                총 <strong className="text-foreground-700">{likedPosts.length}</strong>개의 좋아요한 글
              </p>
            </div>

            <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden divide-y divide-background-300">
              {likedPosts.map((item) => (
                <Link
                  key={item.post_id}
                  to={`/post/${item.post_id}`}
                  className="flex items-center gap-4 px-5 md:px-6 py-4 hover:bg-background-200/50 transition-colors group"
                >
                  <span className="px-2 py-0.5 text-[10px] font-bold bg-red-500/15 text-red-400 rounded whitespace-nowrap flex-shrink-0">
                    {categoryLabels[item.category] || item.category}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-foreground-600 group-hover:text-foreground-700 transition-colors truncate block font-medium">
                      {item.title}
                    </span>
                    <span className="text-[10px] text-foreground-600 mt-0.5 block">
                      {item.author_nickname}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-foreground-600 flex-shrink-0">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-eye-line" />
                      </span>
                      {item.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-thumb-up-line" />
                      </span>
                      {item.likes}
                    </span>
                    <span className="hidden sm:inline text-foreground-600 whitespace-nowrap">{formatDate(item.liked_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}